import { useCallback, useEffect, useRef, useState } from "react";

// Cloudflare Turnstile, loaded only when the server says it's configured (the public link's
// lookup returns a site key), so nothing from challenges.cloudflare.com is requested otherwise.

interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      appearance?: "always" | "execute" | "interaction-only";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing"));
    s.onerror = () => {
      scriptPromise = null; // allow a retry on the next mount
      reject(new Error("turnstile blocked"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export type TurnstileState = "off" | "working" | "ready" | "failed";

/**
 * Attach `containerRef` to an empty element. While `siteKey` is undefined the hook does nothing
 * and reports "off"; otherwise it renders the widget in "interaction-only" mode (people see
 * something only if Cloudflare needs them to act) and keeps `token` current. A token works
 * once, so call `reset()` after any submit attempt.
 */
export function useTurnstile(siteKey: string | undefined) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState("");
  const [state, setState] = useState<TurnstileState>(siteKey ? "working" : "off");

  useEffect(() => {
    if (!siteKey) {
      setState("off");
      return;
    }
    setState("working");
    let cancelled = false;
    loadScript()
      .then((api) => {
        if (cancelled || !containerRef.current) return;
        widgetId.current = api.render(containerRef.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: (t) => {
            setToken(t);
            setState("ready");
          },
          // Tokens expire after a few minutes; a fresh one is issued
          // automatically, so just stop offering the stale one meanwhile.
          "expired-callback": () => {
            setToken("");
            setState("working");
          },
          "error-callback": () => {
            setToken("");
            setState("failed");
          },
        });
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken("");
    if (widgetId.current && window.turnstile) {
      setState("working");
      window.turnstile.reset(widgetId.current);
    }
  }, []);

  return { containerRef, token, state, reset };
}
